import { RegisterOptions } from "react-hook-form";
import { TextFieldConfig, TextAreaFieldConfig } from "models/formField";

/**
 * Common validation messages
 */
export const ValidationMessages = {
  required: "This field is required",
  email: "Please enter a valid email address",
  minLength: (min: number) => `Minimum ${min} characters required`,
  maxLength: (max: number) => `Maximum ${max} characters allowed`,
  min: (min: number) => `Value must be at least ${min}`,
  max: (max: number) => `Value must be at most ${max}`,
  pattern: "Invalid format",
  url: "Please enter a valid URL",
  phone: "Please enter a valid phone number",
};

/**
 * Validation rule builders for common scenarios
 */
export const ValidationRules = {
  /**
   * Email validation
   */
  email: (): RegisterOptions => ({
    pattern: {
      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
      message: ValidationMessages.email,
    },
  }),

  /**
   * URL validation
   */
  url: (): RegisterOptions => ({
    pattern: {
      value: /^https?:\/\/.+/i,
      message: ValidationMessages.url,
    },
  }),

  /**
   * Phone number validation (basic)
   */
  phone: (): RegisterOptions => ({
    pattern: {
      value: /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/,
      message: ValidationMessages.phone,
    },
  }),

  /**
   * Required field validation
   */
  required: (message?: string): RegisterOptions => ({
    required: message || ValidationMessages.required,
  }),

  /**
   * Minimum length validation
   */
  minLength: (min: number, message?: string): RegisterOptions => ({
    minLength: {
      value: min,
      message: message || ValidationMessages.minLength(min),
    },
  }),

  /**
   * Maximum length validation
   */
  maxLength: (max: number, message?: string): RegisterOptions => ({
    maxLength: {
      value: max,
      message: message || ValidationMessages.maxLength(max),
    },
  }),

  /**
   * Minimum value validation (for numbers)
   */
  min: (min: number, message?: string): RegisterOptions => ({
    min: {
      value: min,
      message: message || ValidationMessages.min(min),
    },
  }),

  /**
   * Maximum value validation (for numbers)
   */
  max: (max: number, message?: string): RegisterOptions => ({
    max: {
      value: max,
      message: message || ValidationMessages.max(max),
    },
  }),

  /**
   * Pattern validation
   */
  pattern: (pattern: RegExp, message?: string): RegisterOptions => ({
    pattern: {
      value: pattern,
      message: message || ValidationMessages.pattern,
    },
  }),

  /**
   * Custom validation function
   */
  custom: (
    validator: (value: any) => boolean | string | Promise<boolean | string>,
    message?: string,
  ): RegisterOptions => ({
    validate: async (value) => {
      const result = await validator(value);
      if (result === true) return true;
      if (typeof result === "string") return result;
      return message || "Validation failed";
    },
  }),
};

/**
 * Merge multiple validation rules
 */
export const mergeValidationRules = (
  ...rules: (RegisterOptions | undefined)[]
): RegisterOptions => {
  const result = rules.reduce<RegisterOptions>((merged, rule) => {
    if (!rule) return merged;
    if (!merged || Object.keys(merged).length === 0) return rule;

    // Handle validate functions specially - combine them
    if (rule.validate && merged.validate) {
      const prevValidate = merged.validate;
      const newValidate = rule.validate;

      return {
        ...merged,
        ...rule,
        validate: async (value: any, formValues: any) => {
          // Run previous validations
          const prevResult =
            typeof prevValidate === "function"
              ? await prevValidate(value, formValues)
              : true;
          if (prevResult !== true) return prevResult;

          // Run new validation
          const newResult =
            typeof newValidate === "function"
              ? await newValidate(value, formValues)
              : true;
          return newResult;
        },
      } as RegisterOptions;
    }

    return { ...merged, ...rule } as RegisterOptions;
  }, {} as RegisterOptions);

  return result;
};

/**
 * Build validation rules from field config
 */
export const buildValidationFromConfig = (
  config: TextFieldConfig | TextAreaFieldConfig | any,
): RegisterOptions | undefined => {
  const rules: RegisterOptions[] = [];

  // Add validation from config.validation
  if (config.validation) {
    rules.push(config.validation);
  }

  // Text field specific validations
  if ("maxLength" in config && config.maxLength) {
    rules.push(ValidationRules.maxLength(config.maxLength));
  }

  if ("minLength" in config && config.minLength) {
    rules.push(ValidationRules.minLength(config.minLength));
  }

  if ("max" in config && config.max !== undefined) {
    rules.push(ValidationRules.max(config.max));
  }

  if ("min" in config && config.min !== undefined) {
    rules.push(ValidationRules.min(config.min));
  }

  if ("pattern" in config && config.pattern) {
    rules.push(ValidationRules.pattern(config.pattern));
  }

  return rules.length > 0 ? mergeValidationRules(...rules) : undefined;
};

/**
 * Validation helper for dependent fields
 * Validates that field A matches field B (e.g., password confirmation)
 */
export const matchesField = (
  fieldName: string,
  message = "Fields do not match",
): RegisterOptions => ({
  validate: (value: any, formValues: any) => {
    return value === formValues[fieldName] || message;
  },
});

/**
 * Validation for date ranges
 */
export const DateValidation = {
  /**
   * Validate date is after another date
   */
  after: (date: Date, message?: string): RegisterOptions => ({
    validate: (value: Date) => {
      if (!value) return true;
      return (
        value > date ||
        message ||
        `Date must be after ${date.toLocaleDateString()}`
      );
    },
  }),

  /**
   * Validate date is before another date
   */
  before: (date: Date, message?: string): RegisterOptions => ({
    validate: (value: Date) => {
      if (!value) return true;
      return (
        value < date ||
        message ||
        `Date must be before ${date.toLocaleDateString()}`
      );
    },
  }),

  /**
   * Validate date is between two dates
   */
  between: (
    startDate: Date,
    endDate: Date,
    message?: string,
  ): RegisterOptions => ({
    validate: (value: Date) => {
      if (!value) return true;
      return (
        (value >= startDate && value <= endDate) ||
        message ||
        `Date must be between ${startDate.toLocaleDateString()} and ${endDate.toLocaleDateString()}`
      );
    },
  }),
};
